import { ArrowLeft } from "@phosphor-icons/react";
import clsx from "clsx";
import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
  type HTMLAttributes,
  type JSX,
} from "react";
import { Drawer as DrawerPrimitive } from "vaul";

type DrawerProps = ComponentPropsWithoutRef<typeof DrawerPrimitive.Root> & {
  onAfterClose?: () => void;
};

function Drawer({ onAfterClose = null, onOpenChange, ...props }: DrawerProps) {
  return (
    <DrawerPrimitive.Root
      onOpenChange={(open) => {
        if (onOpenChange) {
          onOpenChange(open);
        }

        if (!open && onAfterClose) {
          setTimeout(() => onAfterClose(), 300);
        }
      }}
      repositionInputs={false}
      {...props}
    />
  );
}
Drawer.displayName = "Drawer";

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = forwardRef<
  ElementRef<typeof DrawerPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={clsx(
      "fixed inset-0 z-dialog bg-black-950/60 backdrop-blur-sm",
      className,
    )}
    {...props}
  />
));
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

function isThirdPartyModalOpen() {
  return !!document.getElementById("Cardinal-ElementContainer");
}

const DrawerContent = forwardRef<
  ElementRef<typeof DrawerPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>
>(
  (
    { className, children, onInteractOutside, onEscapeKeyDown, ...props },
    ref,
  ) => (
    <DrawerPortal>
      <DrawerOverlay />
      <DrawerPrimitive.Content
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          (e.target as HTMLDivElement).focus();
        }}
        onInteractOutside={(e) => {
          if (isThirdPartyModalOpen()) e.preventDefault();

          onInteractOutside?.(e);
        }}
        onEscapeKeyDown={(e) => {
          if (isThirdPartyModalOpen()) e.preventDefault();

          onEscapeKeyDown?.(e);
        }}
        ref={ref}
        className={clsx(
          "fixed inset-x-0 bottom-0 z-dialog mt-24 flex h-auto max-h-dvh flex-col rounded-t-5xl overflow-hidden bg-white focus-visible:outline-none",
          className,
        )}
        {...props}
      >
        {/* Top Section */}
        <div className="absolute left-1/2 top-2 z-10 h-1 w-8 -translate-x-1/2 rounded-full bg-black-200" />

        {/* Content */}
        <div className="flex flex-col overflow-y-auto px-4 pb-4 pt-5 scrollbar-thin">
          {children}
        </div>
      </DrawerPrimitive.Content>
    </DrawerPortal>
  ),
);
DrawerContent.displayName = "DrawerContent";

function DrawerFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={clsx(
        "flex bg-white border-t justify-between border-t-black-200 p-4 shadow-2xl fixed inset-x-0 bottom-0 z-10",
        className,
      )}
      {...props}
    />
  );
}
DrawerFooter.displayName = "DrawerFooter";

function DrawerTitle({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <DrawerPrimitive.Title
      className={clsx("text-center text-xl-bold", className)}
      {...props}
    >
      {children}
    </DrawerPrimitive.Title>
  );
}

function DrawerHeader({
  title,
  subtitle,
  onBack,
  actionComponent,
}: {
  title: string;
  subtitle?: string;
  onBack?: () => void;
  actionComponent?: JSX.Element;
}) {
  return (
    <div className="relative mb-4">
      {onBack && (
        <button
          type="button"
          aria-label="go back"
          onClick={onBack}
          className="left-0 top-1 absolute"
        >
          <ArrowLeft className="size-5 text-black-500" />
        </button>
      )}

      <div>
        <DrawerPrimitive.Title
          className={clsx(
            "text-xl-bold text-center",
            onBack ? "px-14" : "pl-2.5",
          )}
        >
          {title}
        </DrawerPrimitive.Title>

        {subtitle && (
          <DrawerPrimitive.Description className="text-xs-medium text-black-500 mt-2 text-center px-9">
            {subtitle}
          </DrawerPrimitive.Description>
        )}
      </div>

      <div className="right-0 top-1 absolute">{actionComponent}</div>
    </div>
  );
}

function DrawerDescription({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <DrawerPrimitive.Description
      className={clsx(
        "mt-2 text-center text-xs-medium text-black-500",
        className,
      )}
      {...props}
    >
      {children}
    </DrawerPrimitive.Description>
  );
}

export {
  Drawer,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
  DrawerHeader,
};
